import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import cn from "classnames";
import {useImages} from "../../contexts/images";
import {useSetting} from "../../contexts/setting";
import {decodeSvgFromDataUrl} from "../../libs/bg-utils";
import ColorPicker from "../ColorPicker";
import {SVG_GRADIENT_SELECTOR as GRADIENT_SELECTOR, readSvgGradientStops, writeSvgGradientStops} from "../../libs/svgGradients";
import "./index.css";

const BG_SVG_SELECTOR = ".bg-transformer [data-bg-inline-svg] svg";
const WRITE_BACK_DEBOUNCE_MS = 300;

/** Stable, monotonic id generator for stop React keys. */
let stopIdCounter = 0;
const nextStopId = () => `stop-${++stopIdCounter}`;

/** Convert any CSS color the browser understands to a 6-digit hex string
 * the native `<input type="color">` can consume. Uses a throwaway canvas
 * for the parse — covers named colors, hsl(), rgb(), etc. without us
 * having to write a CSS color parser. */
function normalizeColorToHex(value) {
  if (!value) return "#000000";
  const trimmed = String(value).trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [, r, g, b] = trimmed.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillStyle = trimmed;
    const computed = ctx.fillStyle;
    if (/^#[0-9a-f]{6}$/i.test(computed)) return computed.toLowerCase();
    const rgba = computed.match(/^rgba?\(([^)]+)\)$/i);
    if (rgba) {
      const parts = rgba[1].split(",").map((s) => parseFloat(s.trim()));
      const [r, g, b] = parts;
      return (
        "#" +
        [r, g, b]
          .map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0"))
          .join("")
          .toLowerCase()
      );
    }
  } catch {
    // canvas not available
  }
  return "#000000";
}

/** Build initial stops by parsing the first gradient out of an SVG data
 * URL. Returns [] when the input isn't SVG, doesn't have any gradients,
 * or has a gradient with no stops. */
function extractStopsFromCustomBg(customBgUrl) {
  const markup = decodeSvgFromDataUrl(customBgUrl);
  if (!markup) return [];
  try {
    const doc = new DOMParser().parseFromString(markup, "image/svg+xml");
    const gradient = doc.querySelector(GRADIENT_SELECTOR);
    if (!gradient) return [];
    return readSvgGradientStops(gradient).map((stop) => ({
      ...stop,
      id: nextStopId(),
      color: normalizeColorToHex(stop.color),
    }));
  } catch {
    return [];
  }
}

/** Resolve the currently-*displayed* custom-bg URL, regardless of which
 * channel set it. Ads uses `customBgs` (per-template + .main), which the
 * templates render whenever set — independent of bgOption. Other channels
 * use the scalar `customBg`, but that's only on screen when
 * bgOption === "custom"; a leftover customBg from an earlier upload must
 * not light up the panel while a gallery/default background is showing.
 * We don't care which template the URL is for — the panel applies its
 * stops to every gradient in every inline-SVG bg on the page. */
function pickActiveCustomBg({customBg, customBgs, bgOption}) {
  if (bgOption === "custom" && typeof customBg === "string" && customBg) return customBg;
  if (customBgs && typeof customBgs === "object") {
    if (typeof customBgs.main === "string" && customBgs.main) return customBgs.main;
    for (const key of Object.keys(customBgs)) {
      const v = customBgs[key];
      if (typeof v === "string" && v) return v;
    }
  }
  return "";
}

/** Compute a CSS linear-gradient string for the preview bar. We hand-roll
 * rgba from hex+opacity because `<input type="color">` only speaks 6-hex
 * — opacity lives separately. */
function stopsToCssGradient(stops) {
  if (!stops.length) return "linear-gradient(to right, transparent, transparent)";
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  const pieces = sorted.map(({color, opacity, offset}) => {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity}) ${offset}%`;
  });
  return `linear-gradient(to right, ${pieces.join(", ")})`;
}

/** btoa() only accepts Latin1. SVG markup is often UTF-8 (Cyrillic
 * placeholder text, em-dashes from designers, etc.), so encode through
 * TextEncoder first. */
function utf8ToBase64(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Re-serialize a stored SVG data URL with the given stops baked into
 * every <linearGradient>/<radialGradient> it contains, then return a
 * fresh `data:image/svg+xml;base64,…` URL. Returns null for non-SVG
 * input or SVG without any gradients — callers should skip those
 * fields rather than overwrite them with empty SVGs. */
function reencodeSvgWithStops(sourceUrl, stops) {
  const markup = decodeSvgFromDataUrl(sourceUrl);
  if (!markup) return null;
  let doc;
  try {
    doc = new DOMParser().parseFromString(markup, "image/svg+xml");
  } catch {
    return null;
  }
  const root = doc?.documentElement;
  if (!root || root.tagName.toLowerCase() === "parsererror") return null;
  const gradients = root.querySelectorAll(GRADIENT_SELECTOR);
  if (!gradients.length) return null;
  gradients.forEach((grad) => {
    writeSvgGradientStops(grad, stops);
  });
  const serialized = new XMLSerializer().serializeToString(root);
  return `data:image/svg+xml;base64,${utf8ToBase64(serialized)}`;
}

/** Materialize the current stop draft into each Livestream SVG. */
function reencodeCustomBgsWithStops(customBgs, stops) {
  if (!customBgs || typeof customBgs !== "object") return customBgs;
  let nextCustomBgs = customBgs;
  for (const [key, value] of Object.entries(customBgs)) {
    if (typeof value !== "string" || !value) continue;
    const nextValue = reencodeSvgWithStops(value, stops);
    if (!nextValue || nextValue === value) continue;
    if (nextCustomBgs === customBgs) nextCustomBgs = {...customBgs};
    nextCustomBgs[key] = nextValue;
  }
  return nextCustomBgs;
}

/** Rewrite every <linearGradient>/<radialGradient> in every inline-SVG
 * background on the page so its <stop> children match `stops`. Preserves
 * gradient geometry (gradientTransform, x1/y1/x2/y2, fx/fy/cx/cy/r) —
 * we touch *only* the stops, since that's the panel's contract. */
function applyStopsToDom(stops, root = document) {
  // Never strip gradients to nothing — a stopless gradient paints transparent,
  // which blanks multi-gradient artwork (e.g. the livestream ribbon). Callers
  // shouldn't reach here with an empty list, but guard defensively.
  if (!stops.length) return false;
  const svgs = root.querySelectorAll(BG_SVG_SELECTOR);
  if (!svgs.length) return false;
  let touched = false;
  svgs.forEach((svg) => {
    const gradients = svg.querySelectorAll(GRADIENT_SELECTOR);
    gradients.forEach((grad) => {
      writeSvgGradientStops(grad, stops);
      touched = true;
    });
  });
  return touched;
}

const GradientControls = ({scopeRef = null} = {}) => {
  const {
    customBg,
    customBgs,
    editCustomBg,
    editCustomBgs,
    stageCustomBgsSceneDraft,
    clearCustomBgsSceneDraft,
    commitCustomBgsSceneDraft,
  } = useImages();
  const {bgOption, channel, product, type, isControlledBanner} = useSetting();
  const activeUrl = useMemo(
    () => pickActiveCustomBg({customBg, customBgs, bgOption}),
    [customBg, customBgs, bgOption]
  );

  // Stops parsed from the uploaded SVG. Empty list means "no gradients in
  // the active background" — we render null in that case so the panel
  // disappears for non-SVG / non-gradient uploads.
  const [stops, setStops] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const stopsRef = useRef(stops);

  // We persist edits by re-encoding the SVG with updated stops and
  // pushing it through the existing editCustomBg / editCustomBgs setters
  // (those are part of IMAGES_SCENE_FIELDS, so scene save picks them up
  // automatically). The write triggers a context update, which re-runs
  // this effect's parse on the activeUrl change — without a guard we'd
  // overwrite our own in-progress state. Track the last URL we wrote
  // and short-circuit when it matches.
  const selfWrittenUrlsRef = useRef(new Set());
  // The exact `stops` array reference we last set from parsing a background
  // (not a user edit). The apply and write-back effects skip when the current
  // `stops` IS this reference — because pushing parsed stops back would rewrite
  // EVERY gradient in EVERY inline-SVG bg on the page to this one stop-set,
  // which flattens multi-gradient artwork (the livestream ribbon = 16 distinct
  // gradients) and, when the parsed bg has no gradient (a raster upload → empty
  // stops), strips those gradients to nothing and blanks the SVG. User edits
  // (updateStop/add/remove) always produce a NEW array, so they don't match
  // this ref and DO get applied/persisted. Reference identity is used (not a
  // boolean flag) so it can't be cleared out from under the second effect.
  const parsedStopsRef = useRef(null);

  // A pending gradient belongs only to the current Livestream product. Never
  // let it leak into a scene after the toolbar context changes.
  useEffect(() => {
    if (!isControlledBanner) clearCustomBgsSceneDraft();
  }, [channel.value, product.value, type.value, clearCustomBgsSceneDraft, isControlledBanner]);

  // The user can replace the SVG mid-session (re-upload) or switch channels.
  // When activeUrl changes from a value we didn't write, reset state from the
  // new SVG's gradients — but do NOT push anything to the DOM/context here:
  // the freshly inlined SVG already renders its own stops, so re-applying
  // would only flatten/blank it.
  useEffect(() => {
    if (selfWrittenUrlsRef.current.has(activeUrl)) {
      // The change came from our own write-back. Our state is already
      // authoritative; don't reset from the re-parsed SVG.
      return;
    }
    if (!isControlledBanner) clearCustomBgsSceneDraft();
    const initial = extractStopsFromCustomBg(activeUrl);
    parsedStopsRef.current = initial;
    stopsRef.current = initial;
    setStops(initial);
    setSelectedId(initial.length ? initial[0].id : null);
  }, [activeUrl, clearCustomBgsSceneDraft, isControlledBanner]);

  // Push the current stops into every inline-SVG gradient on the page — ONLY
  // for user-driven stop edits. On the initial parse of a newly-loaded
  // background we skip: the SVG already renders its own stops, and applying
  // would flatten a multi-gradient artwork (or blank it, if stops is empty).
  useEffect(() => {
    if (stops === parsedStopsRef.current) return;
    const root = scopeRef ? scopeRef.current : document;
    if (!root) return;
    if (!applyStopsToDom(stops, root)) {
      const raf = requestAnimationFrame(() => applyStopsToDom(stops, root));
      return () => cancelAnimationFrame(raf);
    }
  }, [stops, scopeRef]);

  // ── Persist stops back to context so scene save round-trips them ────
  // Read context values + setters off a ref inside the debounce so the
  // effect's dependency list stays tight (only `stops`) — otherwise the
  // timer would reset on every unrelated images-context update.
  const writeBackCtxRef = useRef({});
  writeBackCtxRef.current = {
    customBg,
    customBgs,
    editCustomBg,
    editCustomBgs,
    bgOption,
    channelValue: isControlledBanner ? null : channel.value,
    commitCustomBgsSceneDraft,
  };

  useEffect(() => {
    if (stops === parsedStopsRef.current) return;
    if (!stops.length) return;
    if (isControlledBanner) return;
    const handle = setTimeout(() => {
      const {
        customBg: cb,
        customBgs: cbs,
        editCustomBg: setCb,
        editCustomBgs: setCbs,
        bgOption: bgOpt,
        channelValue,
        commitCustomBgsSceneDraft: commitCbsDraft,
      } = writeBackCtxRef.current;

      // Rewrite every populated SVG field. Non-SVG fields (raster
      // uploads) return null from reencodeSvgWithStops and get skipped.
      // Only touch the scalar customBg when it's the displayed background
      // (bgOption === "custom") — mirrors pickActiveCustomBg so we never
      // bake stops into a customBg that isn't on screen.
      if (bgOpt === "custom" && typeof cb === "string" && cb) {
        const next = reencodeSvgWithStops(cb, stops);
        if (next && next !== cb) {
          selfWrittenUrlsRef.current.add(next);
          setCb(next);
        }
      }
      let writtenCustomBgs = cbs;
      if (channelValue === "livestream") {
        // The edit was staged synchronously for scene construction. Commit the
        // same draft to React state after the debounce without re-running one
        // dispatch per template.
        writtenCustomBgs = commitCbsDraft();
        for (const value of Object.values(writtenCustomBgs)) {
          if (typeof value === "string" && value) selfWrittenUrlsRef.current.add(value);
        }
      } else if (cbs && typeof cbs === "object") {
        for (const key of Object.keys(cbs)) {
          const v = cbs[key];
          if (typeof v !== "string" || !v) continue;
          const next = reencodeSvgWithStops(v, stops);
          if (!next || next === v) continue;
          selfWrittenUrlsRef.current.add(next);
          // editCustomBgs signature: (id, value, isLocked). `main` is
          // the unlocked/shared bucket; everything else is per-template.
          if (key === "main") setCbs(key, next, false);
          else setCbs(key, next, true);
        }
      }
      // Prune the self-write set so it doesn't grow without bound across
      // a long session. After we've written, only the current activeUrl
      // could possibly come back through; older entries are noise.
      const keep = new Set();
      const cur = pickActiveCustomBg({
        customBg: cb,
        customBgs: writtenCustomBgs,
        bgOption: bgOpt,
      });
      if (cur) keep.add(cur);
      // After context updates settle, the *new* URLs we just emitted
      // will be the next activeUrl candidates, so leave them in too.
      selfWrittenUrlsRef.current.forEach((u) => {
        if (keep.size < 8) keep.add(u);
      });
      selfWrittenUrlsRef.current = keep;
    }, WRITE_BACK_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [stops, isControlledBanner]);

  // ── Stop list mutators ──────────────────────────────────────────────
  const applyUserStops = useCallback(
    (nextStops) => {
      stopsRef.current = nextStops;
      if (isControlledBanner && nextStops.length) {
        const next = reencodeSvgWithStops(activeUrl, nextStops);
        if (next && next !== activeUrl) {
          selfWrittenUrlsRef.current.add(next);
          editCustomBg(next);
        }
      }
      if (!isControlledBanner && channel.value === "livestream" && nextStops.length) {
        // Stage only the cheap resolver during the input event. Danger Zone
        // can materialize it immediately, while normal context write-back
        // remains debounced to keep marker dragging responsive.
        stageCustomBgsSceneDraft((currentCustomBgs) =>
          reencodeCustomBgsWithStops(currentCustomBgs, nextStops)
        );
      }
      setStops(nextStops);
    },
    [channel.value, stageCustomBgsSceneDraft, isControlledBanner, activeUrl, editCustomBg]
  );

  const updateStop = useCallback((id, patch) => {
    applyUserStops(stopsRef.current.map((s) => (s.id === id ? {...s, ...patch} : s)));
  }, [applyUserStops]);

  const addStopAtOffset = useCallback(
    (offset) => {
      const clamped = Math.max(0, Math.min(100, offset));
      // Interpolate color from neighbors so the new stop blends in
      // visually instead of plonking a black square onto the bar.
      const sorted = [...stopsRef.current].sort((a, b) => a.offset - b.offset);
      const left = [...sorted].reverse().find((s) => s.offset <= clamped);
      const right = sorted.find((s) => s.offset >= clamped);
      let color = "#888888";
      let opacity = 1;
      if (left && right && left.id !== right.id) {
        const span = right.offset - left.offset;
        const t = span > 0 ? (clamped - left.offset) / span : 0;
        color = mixHex(left.color, right.color, t);
        opacity = left.opacity + (right.opacity - left.opacity) * t;
      } else if (left) {
        color = left.color;
        opacity = left.opacity;
      } else if (right) {
        color = right.color;
        opacity = right.opacity;
      }
      const id = nextStopId();
      applyUserStops([...stopsRef.current, {id, offset: clamped, color, opacity}]);
      setSelectedId(id);
    },
    [applyUserStops]
  );

  const removeStop = useCallback(
    (id) => {
      applyUserStops(stopsRef.current.filter((s) => s.id !== id));
      setSelectedId((prev) => (prev === id ? null : prev));
    },
    [applyUserStops]
  );

  // ── Marker drag ─────────────────────────────────────────────────────
  const barRef = useRef(null);
  const dragRef = useRef(null);

  const onMarkerPointerDown = useCallback(
    (e, id) => {
      e.stopPropagation();
      e.preventDefault();
      setSelectedId(id);
      const bar = barRef.current;
      if (!bar) return;
      dragRef.current = {id, bar};
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    []
  );

  const onMarkerPointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const rect = drag.bar.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    updateStop(drag.id, {offset: Math.max(0, Math.min(100, pct))});
  }, [updateStop]);

  const onMarkerPointerUp = useCallback((e) => {
    if (dragRef.current) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    }
  }, []);

  const onBarPointerDown = useCallback(
    (e) => {
      // Don't react to clicks that actually started on a marker (those
      // bubble up after stopPropagation… but be defensive in case the
      // listener order changes).
      if (e.target.closest(".gradient-controls__marker")) return;
      const rect = barRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      addStopAtOffset(pct);
    },
    [addStopAtOffset]
  );

  if (!activeUrl || !stops.length) return null;

  const selected = stops.find((s) => s.id === selectedId) || null;
  const cssGradient = stopsToCssGradient(stops);

  return (
    <>
      <span className="label">Gradient stops</span>
      <div className="gradient-controls">
      <div
        className="gradient-controls__bar"
        ref={barRef}
        style={{background: cssGradient}}
        onPointerDown={onBarPointerDown}
        title="Click to add a stop"
      >
        {/* Checkerboard sits behind the gradient so opacity reads
            visually, the same way Figma/Sketch render alpha. */}
        <div className="gradient-controls__bar-alpha" />
        <div className="gradient-controls__bar-fill" style={{background: cssGradient}} />
        <div className="gradient-controls__markers">
          {stops.map((s) => (
            <button
              key={s.id}
              type="button"
              className={cn("gradient-controls__marker", {
                selected: s.id === selectedId,
              })}
              style={{left: `${s.offset}%`, "--stop-color": s.color}}
              onPointerDown={(e) => onMarkerPointerDown(e, s.id)}
              onPointerMove={onMarkerPointerMove}
              onPointerUp={onMarkerPointerUp}
              onPointerCancel={onMarkerPointerUp}
              aria-label={`Stop at ${s.offset.toFixed(0)}%`}
            />
          ))}
        </div>
      </div>

      {selected && (
        <div className="gradient-controls__editor">
          <label className="gradient-controls__row">
            <span className="gradient-controls__row-label">Color</span>
            <ColorPicker
              value={selected.color}
              onChange={(hex) => updateStop(selected.id, {color: hex})}
              ariaLabel="Stop color"
            />
            <input
              type="text"
              value={selected.color}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (/^#[0-9a-f]{6}$/i.test(v)) updateStop(selected.id, {color: v.toLowerCase()});
              }}
              className="gradient-controls__hex"
              spellCheck={false}
            />
          </label>

          <label className="gradient-controls__row">
            <span className="gradient-controls__row-label">Opacity</span>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(selected.opacity * 100)}
              onChange={(e) =>
                updateStop(selected.id, {opacity: Number(e.target.value) / 100})
              }
              className="gradient-controls__slider"
            />
            <span className="gradient-controls__value">{Math.round(selected.opacity * 100)}%</span>
          </label>

          <label className="gradient-controls__row">
            <span className="gradient-controls__row-label">Offset</span>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={selected.offset}
              onChange={(e) => updateStop(selected.id, {offset: Number(e.target.value)})}
              className="gradient-controls__slider"
            />
            <span className="gradient-controls__value">{selected.offset.toFixed(0)}%</span>
          </label>

          <div className="gradient-controls__actions">
            <button
              type="button"
              className="gradient-controls__btn gradient-controls__btn--add"
              onClick={() => addStopAtOffset(selected.offset + 5)}
            >
              + Add stop
            </button>
            <button
              type="button"
              className="gradient-controls__btn gradient-controls__btn--remove"
              onClick={() => removeStop(selected.id)}
              disabled={stops.length <= 2}
              title={stops.length <= 2 ? "A gradient needs at least 2 stops" : "Remove stop"}
            >
              Remove
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

/** Linearly interpolate between two 6-digit hex colors. t in [0,1]. */
function mixHex(a, b, t) {
  const ar = parseInt(a.slice(1, 3), 16);
  const ag = parseInt(a.slice(3, 5), 16);
  const ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16);
  const bg = parseInt(b.slice(3, 5), 16);
  const bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (
    "#" +
    [r, g, bl]
      .map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0"))
      .join("")
  );
}

export default GradientControls;
